import { Capacitor } from '@capacitor/core'
import { LS_CFD_ENABLED, LS_CFD_HARDWARE_MODE } from '@/config/electronShellStorage'

/** @returns {Promise<typeof import('./CustomerDisplay.native.js')>} */
async function getCustomerDisplayNative () {
  return import('./CustomerDisplay.native.js')
}

/** @typedef {'generic'|'sunmi'} CustomerDisplayHardwareMode */

/** @type {CustomerDisplayHardwareMode[]} */
export const CUSTOMER_DISPLAY_HARDWARE_MODES = ['generic', 'sunmi']

function persistElectronShellCfdSettings () {
  if (typeof window === 'undefined' || !window.electronAPI?.isElectron || !window.electronAPI?.mergeShellUserSettings) {
    return
  }
  void window.electronAPI.mergeShellUserSettings({
    customerDisplayEnabled: getCustomerDisplayEnabledFromStorage(),
    customerDisplayHardwareMode: getCustomerDisplayHardwareModeFromStorage()
  })
}

export function isAndroidCustomerDisplayAvailable() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
}

/**
 * Windows packaged Electron: same guard as Volcora (customer display is unsupported on macOS Electron for now).
 */
export function isElectronCustomerDisplayAvailable() {
  if (typeof window === 'undefined') return false
  return !!(window.electronAPI?.isElectron && window.electronAPI?.volcoraSupported)
}

export function isNativeCustomerDisplayAvailable() {
  return isAndroidCustomerDisplayAvailable() || isElectronCustomerDisplayAvailable()
}

export function getCustomerDisplayEnabledFromStorage() {
  try {
    return localStorage.getItem(LS_CFD_ENABLED) === 'true'
  } catch {
    return false
  }
}

export function setCustomerDisplayEnabledInStorage(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(LS_CFD_ENABLED, 'true')
    } else {
      localStorage.removeItem(LS_CFD_ENABLED)
    }
  } catch {
    /* ignore */
  }
  persistElectronShellCfdSettings()
}

/**
 * @returns {CustomerDisplayHardwareMode}
 */
export function getCustomerDisplayHardwareModeFromStorage() {
  try {
    const v = localStorage.getItem(LS_CFD_HARDWARE_MODE)
    return v === 'sunmi' ? 'sunmi' : 'generic'
  } catch {
    return 'generic'
  }
}

/**
 * @param {CustomerDisplayHardwareMode} mode
 */
export function setCustomerDisplayHardwareModeInStorage(mode) {
  try {
    const normalized = mode === 'sunmi' ? 'sunmi' : 'generic'
    if (normalized === 'generic') {
      localStorage.removeItem(LS_CFD_HARDWARE_MODE)
    } else {
      localStorage.setItem(LS_CFD_HARDWARE_MODE, normalized)
    }
  } catch {
    /* ignore */
  }
  persistElectronShellCfdSettings()
}

function defaultDisplayStatus() {
  return {
    enabled: false,
    hardwareMode: 'generic',
    hasSecondaryDisplay: false,
    secondaryDisplayCount: 0,
    presentationDisplayCount: 0,
    secondaryDisplayTouch: false,
    windowOpen: false
  }
}

/**
 * Sync native SharedPreferences with localStorage (call after bridge is ready).
 * Applies hardware mode before enable so the native layer picks the correct attach path.
 */
export async function syncCustomerDisplayNativeFromStorage() {
  if (isAndroidCustomerDisplayAvailable()) {
    const enabled = getCustomerDisplayEnabledFromStorage()
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeSyncCustomerDisplayFromStorage(enabled, getCustomerDisplayHardwareModeFromStorage())
    } catch {
      /* ignore */
    }
    return
  }

  if (!isElectronCustomerDisplayAvailable()) return
  const enabled = getCustomerDisplayEnabledFromStorage()
  try {
    await window.electronAPI.cfdSyncEnabled(enabled)
  } catch {
    /* ignore */
  }
}

export async function customerDisplaySetHardwareMode(mode) {
  if (isElectronCustomerDisplayAvailable()) {
    setCustomerDisplayHardwareModeInStorage('generic')
    return
  }
  setCustomerDisplayHardwareModeInStorage(mode)
  if (!isAndroidCustomerDisplayAvailable()) return
  const native = await getCustomerDisplayNative()
  try {
    await native.nativeSetHardwareMode(getCustomerDisplayHardwareModeFromStorage())
  } catch {
    /* ignore */
  }
}

export async function customerDisplaySetEnabled(enabled) {
  setCustomerDisplayEnabledInStorage(enabled)
  if (isAndroidCustomerDisplayAvailable()) {
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeSetEnabled(enabled, getCustomerDisplayHardwareModeFromStorage())
    } catch {
      /* ignore */
    }
    return
  }
  if (isElectronCustomerDisplayAvailable()) {
    try {
      await window.electronAPI.cfdSyncEnabled(enabled)
    } catch {
      /* ignore */
    }
  }
}

export async function customerDisplaySetBusinessInfo(name, logoUrl) {
  if (isAndroidCustomerDisplayAvailable()) {
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeSetBusinessInfo(name, logoUrl)
    } catch {
      /* ignore */
    }
    return
  }
  if (isElectronCustomerDisplayAvailable()) {
    try {
      await window.electronAPI.cfdSetBusinessInfo({
        name: name ?? null,
        logoUrl: logoUrl ?? null
      })
    } catch {
      /* ignore */
    }
  }
}

/**
 * @param {object} payload
 * @param {Array<{ name: string, quantity: number, price: number, lineItemId?: number|string|null }>} payload.lines
 * @param {string} [payload.greetingLine]
 * @param {number} [payload.discountTotal]
 * @param {string} [payload.discountCaption]
 * @param {number} [payload.baseSubtotal]
 * @param {number} payload.subtotal
 * @param {number} payload.tax
 * @param {number} payload.total
 */
export async function customerDisplayUpdateState(payload) {
  if (!getCustomerDisplayEnabledFromStorage()) return
  if (isAndroidCustomerDisplayAvailable()) {
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeUpdateState(payload)
    } catch {
      /* ignore */
    }
    return
  }
  if (isElectronCustomerDisplayAvailable()) {
    try {
      await window.electronAPI.cfdUpdateState({
        lines: payload.lines ?? [],
        greetingLine: payload.greetingLine ?? '',
        discountTotal: payload.discountTotal,
        discountCaption: payload.discountCaption ?? null,
        baseSubtotal: payload.baseSubtotal,
        subtotal: payload.subtotal ?? 0,
        tax: payload.tax ?? 0,
        total: payload.total ?? 0
      })
    } catch {
      /* ignore */
    }
  }
}

/**
 * @param {object} [options]
 * @param {boolean} [options.offerReceiptChoices] When true, touch customer display shows receipt options.
 * @param {number|string} [options.saleId]
 * @param {number|string|null} [options.customerId]
 * @param {string} [options.prefilledEmail]
 * @param {string} [options.prefilledPhone]
 */
export async function customerDisplayNotifyPaymentComplete(options = {}) {
  if (!getCustomerDisplayEnabledFromStorage()) return
  const offerReceiptChoices = !!options.offerReceiptChoices
  const payload = {
    offerReceiptChoices,
    saleId: options.saleId ?? null,
    customerId: options.customerId ?? null,
    prefilledEmail: options.prefilledEmail ?? '',
    prefilledPhone: options.prefilledPhone ?? ''
  }
  if (isAndroidCustomerDisplayAvailable()) {
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeNotifyPaymentComplete(payload)
    } catch {
      /* ignore */
    }
    return
  }
  if (isElectronCustomerDisplayAvailable()) {
    try {
      await window.electronAPI.cfdNotifyPaymentComplete(payload)
    } catch {
      /* ignore */
    }
  }
}

/** Acknowledge CFD receipt choice processing (Electron clears overlay after ack). */
export async function customerDisplayAckReceiptChoice ({ success, error }) {
  if (isElectronCustomerDisplayAvailable()) {
    try {
      await window.electronAPI.cfdReceiptChoiceAck({ success: !!success, error: error || null })
    } catch {
      /* ignore */
    }
  }
  if (isAndroidCustomerDisplayAvailable() && success) {
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeDismissPaymentComplete()
    } catch {
      /* ignore */
    }
  }
}

/** Clear payment-complete overlay without executing a receipt action. */
export async function customerDisplayDismissPaymentComplete () {
  if (isElectronCustomerDisplayAvailable()) {
    try {
      await window.electronAPI.cfdReceiptChoiceAck({ success: true })
    } catch {
      /* ignore */
    }
    return
  }
  if (isAndroidCustomerDisplayAvailable()) {
    const native = await getCustomerDisplayNative()
    try {
      await native.nativeDismissPaymentComplete()
    } catch {
      /* ignore */
    }
  }
}

export async function customerDisplayGetStatus() {
  if (isElectronCustomerDisplayAvailable()) {
    try {
      const st = await window.electronAPI.cfdGetDisplayStatus(getCustomerDisplayEnabledFromStorage())
      return {
        ...defaultDisplayStatus(),
        ...st,
        windowOpen: st?.windowOpen ?? false
      }
    } catch {
      return defaultDisplayStatus()
    }
  }
  if (!isAndroidCustomerDisplayAvailable()) {
    return defaultDisplayStatus()
  }
  try {
    const native = await getCustomerDisplayNative()
    const st = await native.nativeGetDisplayStatus()
    return { ...defaultDisplayStatus(), ...st, windowOpen: false }
  } catch {
    return defaultDisplayStatus()
  }
}


export const customerDisplayService = {
  isAndroidCustomerDisplayAvailable,
  isElectronCustomerDisplayAvailable,
  isNativeCustomerDisplayAvailable,
  getCustomerDisplayEnabledFromStorage,
  setCustomerDisplayEnabledInStorage,
  getCustomerDisplayHardwareModeFromStorage,
  setCustomerDisplayHardwareModeInStorage,
  syncCustomerDisplayNativeFromStorage,
  setHardwareMode: customerDisplaySetHardwareMode,
  setEnabled: customerDisplaySetEnabled,
  setBusinessInfo: customerDisplaySetBusinessInfo,
  updateState: customerDisplayUpdateState,
  notifyPaymentComplete: customerDisplayNotifyPaymentComplete,
  ackReceiptChoice: customerDisplayAckReceiptChoice,
  dismissPaymentComplete: customerDisplayDismissPaymentComplete,
  getStatus: customerDisplayGetStatus
}
